import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useAuth } from "../AuthContext";
import { PlatformBadge, StatusBadge, PLATFORM_META, inr } from "../platforms";
import { Download, Send, CheckCircle2, XCircle, Zap, Loader2, ArrowLeft, Ban, Truck, Pencil } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-500";
const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB]";

function PartyCard({ title, p }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className={labelCls}>{title}</div>
      <div className="font-semibold text-slate-900 mt-1">{p?.name}</div>
      <div className="text-sm text-slate-600">{p?.address1}{p?.address2 ? `, ${p.address2}` : ""}</div>
      <div className="text-sm text-slate-600">{p?.city} - {p?.pincode}, {p?.state} ({p?.state_code})</div>
      {p?.gstin && <div className="text-sm font-mono text-slate-700 mt-1">GSTIN: {p.gstin}</div>}
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("3");
  const [cancelRemarks, setCancelRemarks] = useState("");
  const [ewbOpen, setEwbOpen] = useState(false);
  const [ewbForm, setEwbForm] = useState({});
  const [partbOpen, setPartbOpen] = useState(false);
  const [partbForm, setPartbForm] = useState({});

  const load = () => api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(() => navigate("/invoices"));
  useEffect(() => { load(); }, [id]);

  if (!inv) return <div className="text-slate-500 text-sm">Loading...</div>;

  const act = async (fn, key) => {
    setBusy(key);
    try { await fn(); await load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const submit = () => act(async () => {
    await api.post(`/invoices/${id}/submit`);
    toast.success("Submitted for approval");
  }, "submit");

  const review = (action, reason) => act(async () => {
    await api.post(`/invoices/${id}/review`, { action, reason });
    toast.success(action === "approve" ? "Invoice approved" : "Invoice rejected");
  }, action);

  const generate = (includeEwb) => act(async () => {
    const { data } = await api.post(`/invoices/${id}/generate-einvoice`, { include_ewb: includeEwb }, { timeout: 120000 });
    if (data.result.success) toast.success(`E-Invoice generated. IRN: ${String(data.result.irn).slice(0, 20)}...`);
    else toast.error("GSTzen rejected the request", { description: String(data.result.message || "").slice(0, 300), duration: 10000 });
  }, "generate");

  const cancelInvoice = () => act(async () => {
    await api.post(`/invoices/${id}/cancel`, { reason: Number(cancelReason), remarks: cancelRemarks }, { timeout: 120000 });
    toast.success("Invoice cancelled");
    setCancelOpen(false);
  }, "cancel");

  const generateEwbOnly = () => act(async () => {
    await api.post(`/invoices/${id}/generate-ewb`, { transport: ewbForm }, { timeout: 120000 });
    toast.success("E-Way Bill generated");
    setEwbOpen(false);
  }, "ewb");

  const updatePartB = () => act(async () => {
    await api.post(`/invoices/${id}/update-partb`, partbForm, { timeout: 120000 });
    toast.success("E-Way Bill Part-B updated");
    setPartbOpen(false);
  }, "partb");

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = inv.invoice_no.replace(/\//g, "-") + ".pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(""); }
  };

  const downloadEwbPdf = async () => {
    setBusy("ewbpdf");
    try {
      const res = await api.get(`/invoices/${id}/ewb-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "EWB-" + inv.invoice_no.replace(/\//g, "-") + ".pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(""); }
  };

  const downloadCombinedPdf = async () => {
    setBusy("combopdf");
    try {
      const res = await api.get(`/invoices/${id}/combined-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = inv.invoice_no.replace(/\//g, "-") + "-with-EWB.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(""); }
  };

  const canEditRoles = user.role === "admin" || user.role === "maker";
  const canReview = (user.role === "admin" || user.role === "checker") && inv.status === "pending_approval";
  const canSubmit = canEditRoles && ["draft", "rejected"].includes(inv.status);
  const canGenerate = ["approved", "einvoice_failed"].includes(inv.status);
  const canCancel = canEditRoles && inv.status !== "cancelled";
  const hasActiveEwb = inv.ewaybill?.ewb_no && inv.ewaybill?.status !== "CANCELLED";
  const canGenEwbOnly = inv.status === "einvoiced" && inv.einvoice?.irn && !hasActiveEwb;
  const canPartB = user.role === "checker" && hasActiveEwb && inv.status !== "cancelled";
  const btn = "inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 disabled:opacity-50";
  const ewbFields = [
    ["transporter_name", "Transporter Name"], ["transporter_id", "Transporter ID (GSTIN)"],
    ["transport_mode", "Mode"], ["vehicle_no", "Vehicle No."],
    ["lr_no", "LR / Transport Doc No."], ["lr_date", "LR Date"], ["distance_km", "Distance KM (0 = auto-calculate)"],
  ];

  return (
    <div data-testid="invoice-detail-page" className="max-w-5xl space-y-6">
      <button onClick={() => navigate("/invoices")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
        <ArrowLeft size={15} /> Invoices
      </button>

      <div className="flex flex-wrap items-center gap-3">
        <PlatformBadge platform={inv.platform} />
        <h1 data-testid="invoice-detail-number" className="text-2xl sm:text-3xl font-bold tracking-tight font-mono text-slate-900">{inv.invoice_no}</h1>
        <StatusBadge status={inv.status} />
        <span className="text-sm text-slate-500">{inv.invoice_date} · {PLATFORM_META[inv.platform]?.name} · by {inv.created_by_name}{inv.executive ? ` · Exec: ${inv.executive}` : ""}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        <button data-testid="download-pdf-btn" onClick={downloadPdf} disabled={busy === "pdf"} className={`${btn} border border-slate-300 hover:border-slate-500 text-slate-700`}>
          {busy === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Invoice PDF
        </button>
        {inv.ewaybill?.ewb_no && (
          <button data-testid="download-ewb-pdf-btn" onClick={downloadEwbPdf} disabled={busy === "ewbpdf"} className={`${btn} border border-slate-300 hover:border-slate-500 text-slate-700`}>
            {busy === "ewbpdf" ? <Loader2 size={15} className="animate-spin" /> : <Truck size={15} />} E-Way Bill PDF
          </button>
        )}
        {inv.ewaybill?.ewb_no && (
          <button data-testid="download-combined-pdf-btn" onClick={downloadCombinedPdf} disabled={busy === "combopdf"} className={`${btn} border border-slate-300 hover:border-slate-500 text-slate-700`}>
            {busy === "combopdf" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Invoice + EWB
          </button>
        )}
        {canSubmit && (
          <>
            <button data-testid="edit-invoice-btn" onClick={() => navigate(`/invoices/${id}/edit`)} disabled={!!busy}
              className={`${btn} border border-slate-300 hover:border-slate-500 text-slate-700`}>
              <Pencil size={15} /> Edit
            </button>
            <button data-testid="submit-invoice-btn" onClick={submit} disabled={!!busy} className={`${btn} bg-[#0047AB] hover:bg-[#003380] text-white`}>
              <Send size={15} /> Submit for Approval
            </button>
          </>
        )}
        {canReview && (
          <>
            <button data-testid="approve-invoice-btn" onClick={() => review("approve")} disabled={!!busy} className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-white`}>
              <CheckCircle2 size={15} /> Approve
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="reject-invoice-btn" disabled={!!busy} className={`${btn} bg-red-600 hover:bg-red-700 text-white`}>
                  <XCircle size={15} /> Reject
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Reject invoice {inv.invoice_no}?</AlertDialogTitle>
                  <AlertDialogDescription>The maker can edit and resubmit after rejection.</AlertDialogDescription>
                </AlertDialogHeader>
                <input data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason (optional)" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="reject-confirm-btn" onClick={() => review("reject", rejectReason)} className="bg-red-600 hover:bg-red-700">Reject</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
        {canGenerate && (
          <>
            <button data-testid="generate-einvoice-ewb-btn" onClick={() => generate(true)} disabled={!!busy} className={`${btn} bg-[#0A192F] hover:bg-[#12233f] text-white`}>
              {busy === "generate" ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />} E-Invoice + E-Way Bill
            </button>
            <button data-testid="generate-einvoice-only-btn" onClick={() => generate(false)} disabled={!!busy} className={`${btn} border border-[#0A192F] text-[#0A192F] hover:bg-slate-100`}>
              <Zap size={15} /> E-Invoice only
            </button>
          </>
        )}
        {canGenEwbOnly && (
          <Dialog open={ewbOpen} onOpenChange={(v) => { setEwbOpen(v); if (v) setEwbForm({ ...inv.transport }); }}>
            <DialogTrigger asChild>
              <button data-testid="generate-ewb-only-btn" disabled={!!busy} className={`${btn} bg-[#0A192F] hover:bg-[#12233f] text-white`}>
                <Truck size={15} /> Generate E-Way Bill
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Generate E-Way Bill for {inv.invoice_no}</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">Will be generated against the existing IRN. Vehicle No. or Transporter ID is required.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {ewbFields.map(([k, label]) => (
                  <div key={k}>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</label>
                    {k === "transport_mode" ? (
                      <select data-testid={`ewb-${k}-input`} value={String(ewbForm[k] || "1")}
                        onChange={(e) => setEwbForm({ ...ewbForm, [k]: e.target.value })}
                        className={`${inputCls} mt-1`}>
                        <option value="1">Road</option>
                        <option value="2">Rail</option>
                        <option value="3">Air</option>
                        <option value="4">Ship</option>
                      </select>
                    ) : (
                    <input data-testid={`ewb-${k}-input`} type={k === "lr_date" ? "date" : "text"}
                      value={ewbForm[k] || ""} onChange={(e) => setEwbForm({ ...ewbForm, [k]: e.target.value })}
                      className={`${inputCls} mt-1`} />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <button data-testid="ewb-submit-btn" disabled={busy === "ewb"} onClick={generateEwbOnly}
                  className={`${btn} bg-[#0047AB] hover:bg-[#003380] text-white`}>
                  {busy === "ewb" ? <Loader2 size={15} className="animate-spin" /> : <Truck size={15} />} Generate
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}
        {canPartB && (
          <Dialog open={partbOpen} onOpenChange={(v) => { setPartbOpen(v); if (v) setPartbForm((f) => ({ ...f, vehicle_no: inv.transport?.vehicle_no || "", trans_doc_no: inv.transport?.lr_no || "" })); }}>
            <DialogTrigger asChild>
              <button data-testid="update-partb-btn" disabled={!!busy} className={`${btn} bg-[#0A192F] hover:bg-[#12233f] text-white`}>
                <Truck size={15} /> Update Part-B (Vehicle)
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Update Part-B — EWB {inv.ewaybill?.ewb_no}</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">Updates the vehicle/transport document on the active E-Way Bill via GSTzen.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">New Vehicle No. *</label>
                  <input data-testid="partb-vehicle-input" value={partbForm.vehicle_no}
                    onChange={(e) => setPartbForm({ ...partbForm, vehicle_no: e.target.value })} className={`${inputCls} mt-1 font-mono`} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Mode</label>
                  <select data-testid="partb-mode-select" value={partbForm.trans_mode} onChange={(e) => setPartbForm({ ...partbForm, trans_mode: e.target.value })} className={`${inputCls} mt-1`}>
                    <option value="1">1 - Road</option><option value="2">2 - Rail</option>
                    <option value="3">3 - Air</option><option value="4">4 - Ship</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Reason *</label>
                  <select data-testid="partb-reason-select" value={partbForm.reason_code} onChange={(e) => setPartbForm({ ...partbForm, reason_code: e.target.value })} className={`${inputCls} mt-1`}>
                    <option value="1">1 - Due to Breakdown</option>
                    <option value="2">2 - Due to Transhipment</option>
                    <option value="3">3 - Others</option>
                    <option value="4">4 - First Time</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Reason Remarks *</label>
                  <input data-testid="partb-remarks-input" value={partbForm.reason_rem}
                    onChange={(e) => setPartbForm({ ...partbForm, reason_rem: e.target.value })} className={`${inputCls} mt-1`} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">LR / Trans Doc No.</label>
                  <input data-testid="partb-lrno-input" value={partbForm.trans_doc_no}
                    onChange={(e) => setPartbForm({ ...partbForm, trans_doc_no: e.target.value })} className={`${inputCls} mt-1`} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Trans Doc Date</label>
                  <input data-testid="partb-lrdate-input" type="date" value={partbForm.trans_doc_date}
                    onChange={(e) => setPartbForm({ ...partbForm, trans_doc_date: e.target.value })} className={`${inputCls} mt-1`} />
                </div>
              </div>
              <div className="flex justify-end">
                <button data-testid="partb-submit-btn" disabled={busy === "partb" || !partbForm.vehicle_no || !partbForm.reason_rem} onClick={updatePartB}
                  className={`${btn} bg-[#0047AB] hover:bg-[#003380] text-white`}>
                  {busy === "partb" ? <Loader2 size={15} className="animate-spin" /> : <Truck size={15} />} Update Part-B
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {canCancel && (
          <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
            <DialogTrigger asChild>
              <button data-testid="cancel-invoice-btn" disabled={!!busy} className={`${btn} border border-red-300 text-red-700 hover:bg-red-50`}>
                <Ban size={15} /> Cancel Invoice
              </button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle>Cancel invoice {inv.invoice_no}?</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">
                {inv.einvoice?.irn
                  ? "This will also CANCEL the IRN (and active E-Way Bill) on the GST portal. IRN cancellation is only allowed within 24 hours of generation."
                  : "The invoice will be marked cancelled. This cannot be undone."}
              </p>
              {inv.einvoice?.irn && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Cancellation Reason</label>
                  <select data-testid="cancel-reason-select" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className={`${inputCls} mt-1`}>
                    <option value="1">1 - Duplicate</option>
                    <option value="2">2 - Data Entry Mistake</option>
                    <option value="3">3 - Order Cancelled</option>
                    <option value="4">4 - Others</option>
                  </select>
                </div>
              )}
              <input data-testid="cancel-remarks-input" value={cancelRemarks} onChange={(e) => setCancelRemarks(e.target.value)}
                placeholder="Remarks (optional)" className={inputCls} />
              <div className="flex justify-end gap-2">
                <button onClick={() => setCancelOpen(false)} className={`${btn} border border-slate-300 text-slate-700`}>Keep Invoice</button>
                <button data-testid="cancel-confirm-btn" disabled={busy === "cancel"} onClick={cancelInvoice}
                  className={`${btn} bg-red-600 hover:bg-red-700 text-white`}>
                  {busy === "cancel" ? <Loader2 size={15} className="animate-spin" /> : <Ban size={15} />} Cancel Invoice
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {inv.status === "cancelled" && (
        <div data-testid="cancelled-banner" className="bg-slate-100 border border-slate-300 text-slate-700 rounded-lg px-4 py-3 text-sm">
          <b>Cancelled</b> by {inv.cancellation?.by} on {inv.cancellation?.at?.slice(0, 10)}
          {inv.cancellation?.irn_cancelled && " · IRN cancelled on GST portal"}
          {inv.cancellation?.remarks && ` · ${inv.cancellation.remarks}`}
        </div>
      )}

      {inv.status === "rejected" && inv.approval?.rejected_reason && (
        <div data-testid="rejection-banner" className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm">
          <b>Rejected by {inv.approval.rejected_by}:</b> {inv.approval.rejected_reason}
        </div>
      )}
      {inv.status === "einvoice_failed" && inv.einvoice_error && (
        <div data-testid="einvoice-error-banner" className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 text-sm break-words">
          <b>GSTzen error:</b> {String(inv.einvoice_error.message).slice(0, 500)}
        </div>
      )}
      {inv.einvoice?.irn && (
        <div data-testid="einvoice-info" className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm space-y-1">
          <div><b>IRN:</b> <span className="font-mono text-xs break-all">{inv.einvoice.irn}</span></div>
          <div><b>Ack No:</b> <span className="font-mono">{inv.einvoice.ack_no}</span> · <b>Ack Date:</b> {inv.einvoice.ack_dt}</div>
          {inv.ewaybill?.ewb_no && <div><b>E-Way Bill:</b> <span className="font-mono">{inv.ewaybill.ewb_no}</span> · Valid upto {inv.ewaybill.valid_upto || "—"}{inv.ewaybill.status === "CANCELLED" ? " · CANCELLED" : ""}</div>}
          {inv.ewaybill?.partb_history?.length > 0 && (
            <div data-testid="partb-history" className="text-xs text-slate-600">
              <b>Part-B updates:</b> {inv.ewaybill.partb_history.map((h, i) => `${h.vehicle_no} (${h.at?.slice(0, 10)} by ${h.by})`).join(" → ")}
            </div>
          )}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <PartyCard title="Bill To" p={inv.bill_to} />
        <PartyCard title="Ship To (Consignee)" p={inv.ship_to} />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className={labelCls}>Platform & Transport</div>
        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm font-mono text-slate-700">
          {Object.entries({ ...inv.platform_fields, ...inv.transport }).filter(([, v]) => v).map(([k, v]) => (
            <span key={k}><span className="text-slate-400">{k.replace(/_/g, " ")}:</span> {String(v)}</span>
          ))}
          {Object.values({ ...inv.platform_fields, ...inv.transport }).every((v) => !v) && <span className="text-slate-400">—</span>}
        </div>
      </div>

      {inv.remarks && (
        <div data-testid="invoice-narration" className="bg-white border border-slate-200 rounded-lg p-4">
          <div className={labelCls}>Narration</div>
          <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{inv.remarks}</p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="bg-[#0A192F] text-white text-left">
              {["#", "SKU / Description", "HSN", "Qty", "Rate", "Taxable", "GST", "Tax Amt", "Total"].map((h) => (
                <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {inv.items.map((it, i) => (
              <tr key={i}>
                <td className="px-3 py-2.5">{i + 1}</td>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-slate-900">{it.description}</div>
                  <div className="text-xs text-slate-500 font-mono">{it.sku_code}{it.upc ? ` · UPC ${it.upc}` : ""}</div>
                </td>
                <td className="px-3 py-2.5 font-mono">{it.hsn}</td>
                <td className="px-3 py-2.5 text-right">{it.qty} {it.unit}</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(it.rate)}</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(it.taxable_value)}</td>
                <td className="px-3 py-2.5 text-right">{it.gst_rate}%</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(it.igst + it.cgst + it.sgst)}</td>
                <td className="px-3 py-2.5 text-right font-mono font-semibold">{inr(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap justify-end gap-x-8 gap-y-2 text-sm">
        <div>Taxable: <span className="font-mono font-semibold">{inr(inv.totals.taxable)}</span></div>
        {inv.tax_type === "IGST"
          ? <div>IGST: <span className="font-mono font-semibold">{inr(inv.totals.igst)}</span></div>
          : <><div>CGST: <span className="font-mono font-semibold">{inr(inv.totals.cgst)}</span></div><div>SGST: <span className="font-mono font-semibold">{inr(inv.totals.sgst)}</span></div></>}
        <div>Round off: <span className="font-mono">{inv.totals.round_off?.toFixed(2)}</span></div>
        <div className="text-base">Grand Total: <span data-testid="detail-grand-total" className="font-mono font-bold text-slate-900">{inr(inv.totals.grand_total)}</span></div>
      </div>
    </div>
  );
}
